import { get } from 'svelte/store';

import { backendName } from '$lib/services/backends';
import { user } from '$lib/services/user/account.svelte';
import { prefs } from '$lib/services/user/prefs.svelte';

import {
  mergeGitbasePrefsSecrets,
  setGitbaseAuthMode,
  setGitbaseSessionSecrets,
} from './session-secrets';

const BOOTSTRAP_URL = '/admin/cms/bootstrap';
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 300000;

/** @type {ReturnType<typeof setInterval> | undefined} */
let bootstrapTimer;

/**
 * Fetch server-held secrets into the in-memory session store and merge into prefs.
 * @returns {Promise<boolean>} Whether secrets were updated.
 */
export async function fetchGitbaseBootstrap() {
  const token = user.account?.token;

  if (!token) {
    return false;
  }

  let response;

  try {
    response = await fetch(BOOTSTRAP_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return false;
  }

  if (!response.ok) {
    return false;
  }

  const data = await response.json();
  let updated = false;

  if (data.apiKeys && typeof data.apiKeys === 'object') {
    setGitbaseSessionSecrets({ apiKeys: data.apiKeys });
    updated = true;
  }

  if (data.deployHookURL) {
    setGitbaseSessionSecrets({
      deployHookURL: data.deployHookURL,
      deployHookAuthHeader: data.deployHookAuthHeader,
    });
    updated = true;
  }

  if (updated) {
    Object.assign(prefs, mergeGitbasePrefsSecrets(prefs));
  }

  return updated;
}

/**
 * Poll bootstrap until secrets are injected or timeout.
 */
export function startGitbaseBootstrapPolling() {
  if (bootstrapTimer) {
    return;
  }

  fetchGitbaseBootstrap();

  bootstrapTimer = setInterval(async () => {
    const ok = await fetchGitbaseBootstrap();

    if (ok && bootstrapTimer) {
      clearInterval(bootstrapTimer);
      bootstrapTimer = undefined;
    }
  }, POLL_INTERVAL_MS);

  setTimeout(() => {
    if (bootstrapTimer) {
      clearInterval(bootstrapTimer);
      bootstrapTimer = undefined;
    }
  }, POLL_TIMEOUT_MS);
}

/**
 * Called after a successful GitBase sign-in.
 * @param {'google' | 'local'} authMode
 */
export async function onGitbaseSignIn(authMode) {
  setGitbaseAuthMode(authMode);

  if (authMode === 'google') {
    startGitbaseBootstrapPolling();
  } else {
    await fetchGitbaseBootstrap();
  }
}

/** @returns {boolean} */
export const isGitbaseLocalAuth = () => get(backendName) === 'local';
